/* eslint-disable no-unused-vars */
/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-button.html";

import "@nuskin/ns-style-elements";

/**
 * <p>The ns-button should be used for all buttons at nuskin.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/279861734/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/279861734/4/latest/aOgsnlEHo1HJBJdffIR2Mi40s8AiqwFYzX6imvyphFR7E1130ndGlo2BlEqMm3dDlq7pNF84sEwjoLrbEqbF2aZglE/Large-Buttons-2x.png" alt="Buttons UX Documentation">
 * </a>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-button theme="primary">Primary Button</ns-button>
 *     <ns-button theme="primary-inverse">Primary Inverse Button</ns-button>
 *     <ns-button theme="primary-hit">Primary Hit Button</ns-button>
 *     <!-- the --button-custom mixin is used in `dev-portal-style.js` -->
 *     <ns-button custom>Custom Button</ns-button>
 *     <br>
 *     <div style="background-color: var(--color-gray-3); padding: 20px; margin-top: 20px;">
 *      <ns-button theme="secondary">Secondary Button</ns-button>
 *      <ns-button theme="secondary-inverse">Secondary Inverse Button</ns-button>
 *      <ns-button theme="secondary-outline">Secondary Outline Button</ns-button>
 *      <ns-button theme="secondary-hit">Secondary Hit Button</ns-button>
 *      <ns-button theme="tertiary-hit">Tertiary Hit Button</ns-button>
 *     </div>
 *     <br>
 *     <ns-button size="short">Short Button</ns-button>
 *     <ns-button theme="primary-hit" size="short">Short Hit Button</ns-button>
 *     <br>
 *     <ns-button fluid>Fluid Button</ns-button>
 *     <ns-button theme="primary-hit" fluid>Fluid Hit Button</ns-button>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      `<ns-button>` provides the following custom properties and mixins for styling:
 *
 *      Custom property            | Description                           | Default
 *      ---------------------------|---------------------------------------|-------------------
 *      `--button-custom`          | Normal state of the pirmary button    | `{}`
 *      `--button-custom-disabled` | Active state for the pirmary button   | `{}`
 *      `--button-custom-hover`    | Hover state for the pirmary button    | `{}`
 *      `--button-custom-active`   | Active state for the pirmary button   | `{}`
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsButton extends PolymerElement {
  constructor() {
    super();
  }

  static get is() {
    return "ns-button";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Adds a dope animation on mousemove over the ns-button
       * Currently not part of the theme
       */
      animate: {
        type: String,
        value: ""
      },
      /**
       * Use to disable the button
       */
      disabled: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Use to make the button the full width of its container
       */
      fluid: Boolean,
      /**
       * Use to hide the button
       */
      hidden: Boolean,
      /**
       * This is where you will go when you click it
       */
      href: {
        type: String,
        value: ""
      },
      /**
       * 'short' option will make the button more compact
       */
      size: String,
      /**
       * _blank opens in a new tab
       */
      target: String,
      /**
       * Changes the style of the button. Options include:
       * `primary`, `primary-inverse`,
       * `secondary`, `secondary-inverse`, `secondary-outline`,
       * `primary-hit`, `secondary-hit`, `tertiary-hit`
       */
      theme: String,
      /**
       * Use to change type of button (button, submit, and reset)
       */
      type: {
        type: String,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * Name of the icon to be displayed inline with the text
       * ie: "icon-bag"
       */
      icon: String
    };
  }

  ready() {
    super.ready();
    if (this.animate === "gradient")
      this.addEventListener("mousemove", (e) => this._animateGradient(e));
  }

  /**
   * This method animates the gradient sliding
   */
  _animateGradient(e) {
    this.updateStyles({
      "--ns-button-gradient-position":
        ((e.pageX - this.offsetLeft) / this.offsetWidth) * 100 + "%"
    });
  }

  /**
   * Disables input
   */
  disable() {
    this.disabled = true;
  }

  /**
   * Enables input
   */
  enable() {
    this.disabled = false;
  }

  /**
   * Handles a click of the button
   */
  _handleClick() {
    if (!this.disabled) {
      let href = this.attributes.href ? this.attributes.href.textContent : "";
      let target = this.attributes.target
        ? this.attributes.target.textContent
        : "";
      if (href.length) {
        if (target === "_blank" && href) {
          window.open(href, target);
        } else if (href) {
          window.location = href;
        }
      }
    }
  }
}

customElements.define(NsButton.is, NsButton);
